/**
 * Typing data in, and changing data that is already here.
 *
 * Two gaps, one screen. A design started from an empty page could only get data from a
 * spreadsheet, so somebody making one document had to make a file first. And data that
 * had been brought in could have its column types corrected and nothing else, so one
 * wrong figure in a CSV meant fixing the file, bringing it in again, and pointing every
 * design at the copy. Both are a grid.
 *
 * Three decisions worth their reasons.
 *
 * **Every cell is text until it is saved.** The grid holds what was typed, and each
 * column reads it with `readEntry` — the same reading the service checks against — so
 * changing a column from Text to Money re-reads what is already there rather than
 * clearing it, and a cell that does not fit is outlined where it is instead of the
 * whole save being refused with a sentence about row 291.
 *
 * **A renamed column keeps its name underneath.** The heading is the label; the
 * identifier a design binds to is fixed when the column is made, so renaming "Amt" to
 * "Amount" does not unbind the table that shows it.
 *
 * **Saving replaces the whole set.** It is stored as one file, and a set of data a
 * person can see all of is a set they can reason about: what is in the grid when they
 * press Save is exactly what every design using it will print.
 */
import { useDeferredValue, useEffect, useMemo, useState, type ClipboardEvent } from "react";
import { entryText, newColumn, readEntry, retype, summarise, type SourceColumn } from "@broadpaper/ingest";
import { api, type DatasetLink } from "../api.js";
import { PROVIDERS } from "../connectors/index.js";
import { reportFailure } from "../App.js";
import { go } from "../router.js";
import { Button, Confirm, Empty, Note, Skeleton, count, toast } from "../ui/index.js";
import { atLimit, upgradeTarget, useWorkspace } from "../workspace.js";
import { CURRENCIES, TYPES } from "./DataReview.js";
import { emptyGrid, gridOf, isGrid, pasteAt, rowsOf } from "./data-edit.js";

/** Rows on screen at once. A grid of fifty thousand inputs is not a page a browser can draw. */
const PAGE = 50;

const NUMERIC = new Set(["number", "currency", "percent"]);

export function DataEdit({ id, designId }: { id: string | null; designId: string | null }) {
  const { view, refresh } = useWorkspace();
  const [name, setName] = useState("");
  /** The file this data is refreshed from, which a refresh will read over anything changed here. */
  const [link, setLink] = useState<DatasetLink | null>(null);
  const [columns, setColumns] = useState<SourceColumn[] | null>(null);
  const [cells, setCells] = useState<string[][]>([]);
  const [page, setPage] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [removing, setRemoving] = useState<number | null>(null);
  /** The column just added, so its heading box has the cursor in it. */
  const [added, setAdded] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      const start = emptyGrid();
      setColumns(start.columns);
      setCells(start.cells);
      setName("My data");
      return;
    }
    let cancelled = false;
    api
      .datasetData(id)
      .then((loaded) => {
        if (cancelled) return;
        setName(loaded.dataset.name);
        setLink(loaded.dataset.link);
        setColumns(loaded.columns);
        const grid = gridOf(loaded.columns, loaded.rows);
        // Always a row to type into, even in a set of data that has none.
        setCells(grid.length ? grid : [loaded.columns.map(() => "")]);
      })
      .catch((e) => !cancelled && setFailed(e instanceof Error ? e.message : "We couldn't open this."));
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Closing the tab on an unsaved grid asks first. Typing forty rows is not a thing to
  // lose to a stray Ctrl+W.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  // Counted a beat behind the typing. Reading every cell of a large set on each key
  // press would make the box being typed into lag, and the count can afford to wait.
  const settled = useDeferredValue(cells);
  const problems = useMemo(() => (columns ? rowsOf(columns, settled).problems : []), [columns, settled]);

  const back = designId ? `/designs/${designId}` : id ? `/data/${id}` : "/data";

  if (failed) {
    return (
      <Empty title="We couldn't open this set of data" actions={<a className="btn btn--primary" href="/data">Back to your data</a>}>
        {failed}
      </Empty>
    );
  }

  if (!columns) {
    return (
      <div className="wrap stack" style={{ gap: 20 }}>
        <div className="skeleton" style={{ height: 40, width: 320 }} />
        <Skeleton rows={1} height={340} />
      </div>
    );
  }

  const full = !id && atLimit(view, "datasets");
  const upgrade = upgradeTarget(view);
  const tooMany = cells.length > view.limits.rowsPerDataset;

  const changeCells = (update: (current: string[][]) => string[][]) => {
    setCells(update);
    setDirty(true);
  };

  const setCell = (row: number, column: number, text: string) =>
    changeCells((current) => current.map((line, r) => (r === row ? line.map((value, c) => (c === column ? text : value)) : line)));

  const setColumn = (index: number, next: SourceColumn) => {
    setColumns((current) => current!.map((column, i) => (i === index ? next : column)));
    setDirty(true);
  };

  /*
   * A new type re-reads what is already in the column.
   *
   * Each cell that reads as the new type is rewritten the way that type writes it —
   * `1,200` becomes `1200`, `31/08/2026` becomes a date — and a cell that does not is
   * left exactly as it was and outlined, so nothing somebody typed disappears because
   * they chose a type before finishing the column.
   */
  const setType = (index: number, type: SourceColumn["type"]) => {
    const after = retype(columns[index]!, type);
    changeCells((current) =>
      current.map((line) =>
        line.map((text, c) => {
          if (c !== index) return text;
          const entry = readEntry(after, text);
          return entry.ok && entry.value !== null ? entryText(after, entry.value) : text;
        })
      )
    );
    setColumn(index, after);
  };

  const addColumn = () => {
    const column = newColumn(columns, `Column ${columns.length + 1}`);
    setColumns([...columns, column]);
    setAdded(column.name);
    changeCells((current) => current.map((line) => [...line, ""]));
  };

  const removeColumn = (index: number) => {
    setColumns(columns.filter((_, i) => i !== index));
    changeCells((current) => current.map((line) => line.filter((_, c) => c !== index)));
    setRemoving(null);
  };

  const addRow = () => {
    changeCells((current) => [...current, columns.map(() => "")]);
    setPage(Math.floor(cells.length / PAGE));
  };

  const removeRow = (row: number) => {
    changeCells((current) => {
      const next = current.filter((_, r) => r !== row);
      return next.length ? next : [columns.map(() => "")];
    });
    setPage((p) => Math.min(p, Math.max(0, Math.ceil((cells.length - 1) / PAGE) - 1)));
  };

  const paste = (row: number, column: number, text: string): boolean => {
    if (!isGrid(text)) return false;
    changeCells((current) => pasteAt(columns, current, row, column, text));
    return true;
  };

  const save = async () => {
    const read = rowsOf(columns, cells);
    const first = read.problems[0];
    if (first) {
      setPage(Math.floor(first.row / PAGE));
      toast(`Row ${count(first.row + 1)}, ${columns[first.column]!.label}: ${first.reason}`, "bad");
      return;
    }
    if (columns.some((c) => !c.label.trim())) return toast("Every column needs a heading before it can be saved.", "info");
    if (!read.rows.length) return toast("Type at least one row before saving.", "info");

    setSaving(true);
    try {
      const described = summarise(
        columns.map((c) => ({ ...c, label: c.label.trim() })),
        read.rows
      );
      const title = name.trim() || "My data";
      let datasetId = id;
      if (id) {
        await api.replaceDataset(id, { name: title, columns: described, rows: read.rows });
      } else {
        const created = await api.createDataset({ name: title, source: "manual", columns: described, rows: read.rows, headerRow: true });
        datasetId = created.dataset.id;
        // Typed in from a design that had no data: that design is what it was for.
        if (designId) await api.saveTemplate(designId, { datasetId });
      }
      await refresh();
      setDirty(false);
      toast(id ? "Saved. Every design using this data shows the change." : "Saved.", "good");
      go(designId ? `/designs/${designId}` : `/data/${datasetId}`);
    } catch (e) {
      setSaving(false);
      reportFailure(e, "We couldn't save that.");
    }
  };

  const start = page * PAGE;
  const visible = cells.slice(start, start + PAGE);
  const pages = Math.max(1, Math.ceil(cells.length / PAGE));

  return (
    <div className="wrap--wide stack" style={{ gap: 20 }}>
      <div className="head">
        <div className="head__title">
          <span className="eyebrow">
            <a href={back} style={{ textDecoration: "none" }}>
              {designId ? "Back to the design" : id ? "Back to this data" : "Your data"}
            </a>{" "}
            · {count(cells.length)} row{cells.length === 1 ? "" : "s"}
          </span>
          <input
            className="input input--lg"
            value={name}
            maxLength={200}
            onChange={(e) => {
              setName(e.target.value);
              setDirty(true);
            }}
            aria-label="Name"
            style={{ fontFamily: "var(--font-display)", fontSize: "1.8rem", border: "1px solid transparent", background: "transparent", padding: "2px 6px", marginLeft: -6, maxWidth: "22ch" }}
          />
          <p className="head__lede">
            {id
              ? "Change any cell, add or remove rows and columns. Nothing changes until you save — then every design using this data shows it."
              : "Name the columns, choose what each one holds, and type the rows. Paste rows out of a spreadsheet into any cell and they fill in from there."}
          </p>
        </div>
        <div className="head__actions">
          <a className="btn btn--quiet" href={back}>
            Cancel
          </a>
          <Button variant="primary" icon="check" busy={saving} disabled={full || tooMany || (Boolean(id) && !dirty)} onClick={() => void save()}>
            {id && !dirty ? "Saved" : "Save"}
          </Button>
        </div>
      </div>

      {full ? (
        <Note tone="ask" title={`You are using all ${view.limits.datasets} sets of data your plan includes`}>
          Delete one you no longer need{upgrade ? `, or move to ${upgrade.name} for ${count(upgrade.limits.datasets)}.` : "."}
        </Note>
      ) : null}

      {/* Said before anybody types, not after: a correction made here to a figure the
          file still has wrong is gone at the next refresh. */}
      {link ? (
        <Note tone="info" title={`This data is refreshed from “${link.name}” in ${PROVIDERS[link.provider].name}`}>
          Changes you make here last until the next refresh, which brings the rows in from the file again. To keep a change, make it in the file as well.
        </Note>
      ) : null}

      {tooMany ? (
        <Note tone="ask" title={`Your plan holds ${count(view.limits.rowsPerDataset)} rows in one set of data`}>
          This has {count(cells.length)}. Remove the rows this document does not need{upgrade ? `, or move to ${upgrade.name}` : ""}.
        </Note>
      ) : null}

      {problems.length ? (
        <Note tone="ask" title={problems.length === 1 ? "One cell doesn't fit its column" : `${count(problems.length)} cells don't fit their columns`}>
          They are outlined in red — the first is row {count(problems[0]!.row + 1)} of “{columns[problems[0]!.column]?.label}”. Change the cell, or what the
          column holds, and save again.
        </Note>
      ) : null}

      <div className="table__scroll data-grid">
        <table className="table">
          <thead>
            <tr>
              <th className="num">#</th>
              {columns.map((column, c) => (
                <th key={column.name}>
                  <div className="stack" style={{ gap: 4 }}>
                    <div className="row" style={{ gap: 2 }}>
                      <input
                        className="input input--sm"
                        value={column.label}
                        autoFocus={column.name === added}
                        maxLength={400}
                        onChange={(e) => setColumn(c, { ...column, label: e.target.value })}
                        aria-label={`Heading of column ${c + 1}`}
                      />
                      <Button
                        size="sm"
                        variant="quiet"
                        icon="trash"
                        title={`Remove “${column.label}”`}
                        disabled={columns.length === 1}
                        onClick={() => (cells.some((line) => line[c]?.trim()) ? setRemoving(c) : removeColumn(c))}
                      />
                    </div>
                    <div className="row" style={{ gap: 4 }}>
                      <select className="select input--sm" value={column.type} onChange={(e) => setType(c, e.target.value as SourceColumn["type"])} aria-label={`What “${column.label}” holds`}>
                        {TYPES.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                      {column.type === "currency" ? (
                        <select
                          className="select input--sm"
                          style={{ width: 84 }}
                          value={column.currency ?? "GBP"}
                          onChange={(e) => setColumn(c, { ...column, currency: e.target.value })}
                          aria-label={`Currency of “${column.label}”`}
                        >
                          {CURRENCIES.map((code) => (
                            <option key={code} value={code}>
                              {code}
                            </option>
                          ))}
                        </select>
                      ) : null}
                    </div>
                    {/* Turned off on the review screen. Kept here, because saving without
                        it would delete its values. */}
                    {!column.include ? <span className="small muted">Not offered to designs</span> : null}
                  </div>
                </th>
              ))}
              <th>
                <Button size="sm" icon="plus" onClick={addColumn}>
                  Column
                </Button>
              </th>
            </tr>
          </thead>
          <tbody>
            {visible.map((line, i) => {
              const row = start + i;
              return (
                <tr key={row}>
                  <td className="num small muted">{count(row + 1)}</td>
                  {columns.map((column, c) => (
                    <td key={column.name}>
                      <Cell column={column} text={line[c] ?? ""} label={`${column.label}, row ${row + 1}`} onChange={(text) => setCell(row, c, text)} onPaste={(text) => paste(row, c, text)} />
                    </td>
                  ))}
                  <td>
                    <Button size="sm" variant="quiet" icon="cross" title={`Remove row ${row + 1}`} onClick={() => removeRow(row)} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="spread" style={{ flexWrap: "wrap", gap: 10 }}>
        <Button icon="plus" onClick={addRow}>
          Add a row
        </Button>
        {pages > 1 ? (
          <div className="row" style={{ gap: 8, alignItems: "center" }}>
            <span className="small muted num">
              {count(start + 1)}–{count(Math.min(start + PAGE, cells.length))} of {count(cells.length)}
            </span>
            <Button size="sm" variant="quiet" icon="back" title="The rows before" disabled={page === 0} onClick={() => setPage(page - 1)} />
            <Button size="sm" variant="quiet" icon="chevron" title="The rows after" disabled={page >= pages - 1} onClick={() => setPage(page + 1)} />
          </div>
        ) : null}
      </div>

      {removing !== null ? (
        <Confirm title={`Remove “${columns[removing]?.label}”?`} confirmLabel="Remove it" onCancel={() => setRemoving(null)} onConfirm={() => removeColumn(removing)}>
          Its values go when you save. A design showing this column shows a blank there instead, and its Fields screen lets you choose something else.
        </Confirm>
      ) : null}
    </div>
  );
}

/**
 * One cell, as the box its column's type wants.
 *
 * A date is a date picker and a yes-or-no a choice, because a box that cannot hold a
 * wrong value is better than one that outlines it. Except when it already holds one: a
 * date picker given text it cannot read shows nothing, so a cell that does not fit is
 * always a plain box, where the person can see what is wrong with it.
 */
function Cell({ column, text, label, onChange, onPaste }: { column: SourceColumn; text: string; label: string; onChange: (text: string) => void; onPaste: (text: string) => boolean }) {
  const entry = readEntry(column, text);
  const reason = entry.ok ? undefined : entry.reason;
  const className = `input input--sm data-grid__cell${NUMERIC.has(column.type) ? " data-grid__cell--num" : ""}${reason ? " data-grid__cell--bad" : ""}`;
  const pasted = (e: ClipboardEvent<HTMLElement>) => {
    if (onPaste(e.clipboardData.getData("text/plain"))) e.preventDefault();
  };

  if (column.type === "boolean" && !reason) {
    return (
      <select className={className} value={text === "" ? "" : entry.ok && entry.value ? "Yes" : "No"} onChange={(e) => onChange(e.target.value)} onPaste={pasted} aria-label={label}>
        <option value="" />
        <option value="Yes">Yes</option>
        <option value="No">No</option>
      </select>
    );
  }

  const type = !reason && column.type === "date" ? "date" : !reason && column.type === "datetime" ? "datetime-local" : "text";
  return (
    <input
      className={className}
      type={type}
      value={text}
      inputMode={NUMERIC.has(column.type) ? "decimal" : undefined}
      onChange={(e) => onChange(e.target.value)}
      onPaste={pasted}
      aria-label={label}
      aria-invalid={reason ? true : undefined}
      title={reason}
    />
  );
}
